import { useCallback, useEffect, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Calculation, UpdateCalculationBody } from "../types/calculations";
import { useCalculationsApi } from "../useCalculations";
import type { LShapeCutoutCorner } from "../../pages/engines/deckDesigner/deckGeometry";
import {
  buildOutline,
  getFootprintFromOutline,
  isSlopedTerrain,
  type SlopeArrow,
} from "../../pages/engines/deckDesigner/deckOutline";
import { UNIT_SYSTEM_METRIC } from "../../types";
import { Configuration, DIFFERENT_BOARD_POSITION_END } from "./types/configurations";

// The outline is what's saved, but the form edits the deck through L1/L2/l1/l2 and the L's
// orientation: these are derived from the outline on load and turned back into it on save. Same for
// the single slope the form edits, split into its degrees and its arrow.
export type DeckDesignerConfigurationFormValues = Omit<Configuration, "outline" | "slopes"> & {
  base: number | null;
  height: number | null;
  secondaryBase: number | null;
  secondaryHeight: number | null;
  lShapeCutoutCorner: LShapeCutoutCorner;
  slopeDegrees: number | null;
  slopeArrow: SlopeArrow | null;
};

const keepDefined = <T>(value: T | undefined, fallback: T): T => {
  return value === undefined ? fallback : value;
};

export function buildDeckDesignerConfigurationDefaults(
  calculation?: Calculation<Configuration>,
): DeckDesignerConfigurationFormValues {
  const d = calculation?.data;
  const footprint = getFootprintFromOutline(d?.shape ?? null, d?.outline ?? null);
  const slope = d?.slopes?.[0];

  return {
    id: d?.id ?? calculation?.id ?? "",
    name: d?.name ?? "Configuration",
    unitSystem: d?.unitSystem ?? UNIT_SYSTEM_METRIC,
    liveLoad: d?.liveLoad ?? null,
    deflection: d?.deflection ?? null,
    shape: d?.shape ?? null,
    ...footprint,
    totalHeight: d?.totalHeight ?? null,
    slopeDegrees: slope?.slopeDegrees ?? 0,
    slopeArrow: slope
      ? { startXMm: slope.startXMm, startYMm: slope.startYMm, endXMm: slope.endXMm, endYMm: slope.endYMm }
      : null,
    hasHorizontalBoards: d?.hasHorizontalBoards ?? true,
    fasteningType: d?.fasteningType ?? null,
    groovingType: d?.groovingType ?? null,
    gap: d?.gap ?? null,
    connectorFamily: d?.connectorFamily ?? d?.connector?.connectorFamily ?? null,
    connector: d?.connector ?? null,
    connectorSubstructureScrew: d?.connectorSubstructureScrew ?? null,
    connectorBoardScrew: d?.connectorBoardScrew ?? null,
    boardWidth: d?.boardWidth ?? null,
    longDifferentBoardWidth: d?.longDifferentBoardWidth ?? null,
    shortDifferentBoardWidth: d?.shortDifferentBoardWidth ?? null,
    longDifferentBoardPosition: d?.longDifferentBoardPosition ?? DIFFERENT_BOARD_POSITION_END,
    shortDifferentBoardPosition: d?.shortDifferentBoardPosition ?? DIFFERENT_BOARD_POSITION_END,
    boardThickness: d?.boardThickness ?? null,
    groovingHeight: d?.groovingHeight ?? null,
    substructureMaterial: d?.substructureMaterial ?? null,
    substructureMaterialClass: d?.substructureMaterialClass ?? null,
    substructure: d?.substructure ?? null,
    joistsSpacing: d?.joistsSpacing ?? null,
    secondaryItem: d?.secondaryItem ?? null,
    supportFamily: d?.supportFamily ?? d?.support?.supportFamily ?? null,
    support: d?.support ?? null,
    supportScrew: d?.supportScrew ?? null,
  };
}

const mergeDeckDesignerConfigurationValues = (
  values: Partial<DeckDesignerConfigurationFormValues> | undefined,
  fallback: DeckDesignerConfigurationFormValues,
): DeckDesignerConfigurationFormValues => ({
  id: keepDefined(values?.id, fallback.id),
  name: keepDefined(values?.name, fallback.name) || "Configuration",
  unitSystem: keepDefined(values?.unitSystem, fallback.unitSystem),
  liveLoad: keepDefined(values?.liveLoad, fallback.liveLoad),
  deflection: keepDefined(values?.deflection, fallback.deflection),
  shape: keepDefined(values?.shape, fallback.shape),
  base: keepDefined(values?.base, fallback.base),
  height: keepDefined(values?.height, fallback.height),
  secondaryBase: keepDefined(values?.secondaryBase, fallback.secondaryBase),
  secondaryHeight: keepDefined(values?.secondaryHeight, fallback.secondaryHeight),
  lShapeCutoutCorner: keepDefined(values?.lShapeCutoutCorner, fallback.lShapeCutoutCorner),
  totalHeight: keepDefined(values?.totalHeight, fallback.totalHeight),
  slopeDegrees: keepDefined(values?.slopeDegrees, fallback.slopeDegrees),
  slopeArrow: keepDefined(values?.slopeArrow, fallback.slopeArrow),
  hasHorizontalBoards: keepDefined(values?.hasHorizontalBoards, fallback.hasHorizontalBoards),
  fasteningType: keepDefined(values?.fasteningType, fallback.fasteningType),
  groovingType: keepDefined(values?.groovingType, fallback.groovingType),
  gap: keepDefined(values?.gap, fallback.gap),
  connectorFamily: keepDefined(values?.connectorFamily, fallback.connectorFamily),
  connector: keepDefined(values?.connector, fallback.connector),
  connectorSubstructureScrew: keepDefined(values?.connectorSubstructureScrew, fallback.connectorSubstructureScrew),
  connectorBoardScrew: keepDefined(values?.connectorBoardScrew, fallback.connectorBoardScrew),
  boardWidth: keepDefined(values?.boardWidth, fallback.boardWidth),
  longDifferentBoardWidth: keepDefined(values?.longDifferentBoardWidth, fallback.longDifferentBoardWidth),
  shortDifferentBoardWidth: keepDefined(values?.shortDifferentBoardWidth, fallback.shortDifferentBoardWidth),
  longDifferentBoardPosition: keepDefined(values?.longDifferentBoardPosition, fallback.longDifferentBoardPosition),
  shortDifferentBoardPosition: keepDefined(values?.shortDifferentBoardPosition, fallback.shortDifferentBoardPosition),
  boardThickness: keepDefined(values?.boardThickness, fallback.boardThickness),
  groovingHeight: keepDefined(values?.groovingHeight, fallback.groovingHeight),
  substructureMaterial: keepDefined(values?.substructureMaterial, fallback.substructureMaterial),
  substructureMaterialClass: keepDefined(values?.substructureMaterialClass, fallback.substructureMaterialClass),
  substructure: keepDefined(values?.substructure, fallback.substructure),
  joistsSpacing: keepDefined(values?.joistsSpacing, fallback.joistsSpacing),
  secondaryItem: keepDefined(values?.secondaryItem, fallback.secondaryItem),
  supportFamily: keepDefined(values?.supportFamily, fallback.supportFamily),
  support: keepDefined(values?.support, fallback.support),
  supportScrew: keepDefined(values?.supportScrew, fallback.supportScrew),
});

const serializeForComparison = (v: DeckDesignerConfigurationFormValues): string =>
  JSON.stringify({
    id: v.id,
    name: v.name,
    unitSystem: v.unitSystem,
    liveLoad: v.liveLoad,
    deflection: v.deflection,
    shape: v.shape,
    base: v.base,
    height: v.height,
    secondaryBase: v.secondaryBase,
    secondaryHeight: v.secondaryHeight,
    lShapeCutoutCorner: v.lShapeCutoutCorner,
    totalHeight: v.totalHeight,
    slopeDegrees: v.slopeDegrees,
    slopeArrow: v.slopeArrow,
    hasHorizontalBoards: v.hasHorizontalBoards,
    fasteningType: v.fasteningType,
    groovingType: v.groovingType,
    gap: v.gap,
    connectorFamily: v.connectorFamily,
    connector: v.connector,
    connectorSubstructureScrew: v.connectorSubstructureScrew,
    connectorBoardScrew: v.connectorBoardScrew,
    boardWidth: v.boardWidth,
    longDifferentBoardWidth: v.longDifferentBoardWidth,
    shortDifferentBoardWidth: v.shortDifferentBoardWidth,
    longDifferentBoardPosition: v.longDifferentBoardPosition,
    shortDifferentBoardPosition: v.shortDifferentBoardPosition,
    boardThickness: v.boardThickness,
    groovingHeight: v.groovingHeight,
    substructureMaterial: v.substructureMaterial,
    substructureMaterialClass: v.substructureMaterialClass,
    substructure: v.substructure,
    joistsSpacing: v.joistsSpacing,
    secondaryItem: v.secondaryItem,
    supportFamily: v.supportFamily,
    support: v.support,
    supportScrew: v.supportScrew,
  });

// The payload for the form: the outline instead of L1/L2/l1/l2 and the L's orientation, and a flat
// terrain (no slopes) until both the slope and its arrow are set.
const toConfiguration = (values: DeckDesignerConfigurationFormValues): Configuration => {
  const {
    base,
    height,
    secondaryBase,
    secondaryHeight,
    lShapeCutoutCorner,
    slopeDegrees,
    slopeArrow,
    ...configuration
  } = values;

  return {
    ...configuration,
    outline: buildOutline({ shape: values.shape, base, height, secondaryBase, secondaryHeight, lShapeCutoutCorner }),
    slopes: isSlopedTerrain(values) ? [{ slopeDegrees: slopeDegrees!, ...slopeArrow! }] : [],
  };
};

const AUTO_SAVE_DELAY_MS = 350;

// Fire-and-forget: no saving state is kept, so a save never re-renders the designer while it starts
// or once the response arrives - only a failure is surfaced, through onError. Nor does a change: the
// values and their validity come from a form subscription, outside render (useWatch/useFormState
// re-rendered the host, and so the whole form, on every change).
export function useAutoSaveDeckDesignerConfiguration(
  calculation: Calculation<Configuration> | undefined,
  formMethods: Pick<UseFormReturn<DeckDesignerConfigurationFormValues>, "control" | "getValues" | "subscribe">,
  onError?: (error: unknown) => void,
  // Checked on the very values about to be saved (no render lag): false skips the save, e.g. while the
  // connector selections match no connector mapping.
  canSave?: (values: DeckDesignerConfigurationFormValues) => boolean,
): void {
  const { control, getValues, subscribe } = formMethods;
  const canSaveRef = useRef(canSave);
  const { updateCalculation } = useCalculationsApi();
  const onErrorRef = useRef(onError);
  const lastSerializedRef = useRef(
    calculation ? serializeForComparison(buildDeckDesignerConfigurationDefaults(calculation)) : "",
  );
  // Whether lastSerializedRef already holds a real baseline - true immediately when calculation is
  // available on first render, false while still waiting on it to load.
  const hasBaselineRef = useRef(calculation !== undefined);
  // The latest of each, for the subscription below, which outlives the render it was set up in.
  const calculationRef = useRef(calculation);
  const isValidRef = useRef(control._formState.isValid);
  const saveTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!calculation) return;

    // calculation gets a new object reference on every save too (the optimistic mutate, then the
    // real one), not just when it first loads. Resyncing the baseline from the server's echoed
    // response on every one of those - rather than only once, when the data first arrives - meant any
    // normalization drift between what we sent and what the server echoed back (float rounding,
    // populated nested fields, etc) made the very next tick see "unsaved changes" that don't exist,
    // re-triggering the save below forever. So capture the baseline once and leave it alone.
    if (!hasBaselineRef.current) {
      hasBaselineRef.current = true;
      lastSerializedRef.current = serializeForComparison(buildDeckDesignerConfigurationDefaults(calculation));
    }
  }, [calculation]);

  // Restarts the debounce for these values: a newer change (or calculation) cancels the pending save,
  // which then goes out only if the values are valid and differ from the last saved ones.
  const scheduleSave = useCallback(
    (values: Partial<DeckDesignerConfigurationFormValues>) => {
      window.clearTimeout(saveTimerRef.current);

      const currentCalculation = calculationRef.current;
      if (!currentCalculation || !currentCalculation.canEdit || !isValidRef.current) return;

      const fallbackValues = buildDeckDesignerConfigurationDefaults(currentCalculation);
      const nextValues = mergeDeckDesignerConfigurationValues(values, fallbackValues);
      if (canSaveRef.current && !canSaveRef.current(nextValues)) return;

      const nextSerialized = serializeForComparison(nextValues);

      if (nextSerialized === lastSerializedRef.current) return;

      saveTimerRef.current = window.setTimeout(() => {
        lastSerializedRef.current = nextSerialized;
        const nextConfiguration = toConfiguration(nextValues);

        const body: UpdateCalculationBody<Configuration> = {
          name: currentCalculation.name,
          description: currentCalculation.description ?? null,
          data: nextConfiguration,
        };

        void updateCalculation(currentCalculation.project?.id, currentCalculation.id, body, {
          ...currentCalculation,
          data: nextConfiguration,
        }).catch((err) => onErrorRef.current?.(err));
      }, AUTO_SAVE_DELAY_MS);
    },
    [updateCalculation],
  );

  useEffect(() => {
    const unsubscribe = subscribe({
      formState: { values: true, isValid: true },
      callback: ({ values, isValid }) => {
        if (isValid !== undefined) {
          isValidRef.current = isValid;
        }
        scheduleSave(values);
      },
    });
    // The cards' effects ran before this one and may have changed it since the render.
    isValidRef.current = control._formState.isValid;

    return () => {
      unsubscribe();
      window.clearTimeout(saveTimerRef.current);
    };
  }, [control, scheduleSave, subscribe]);

  // What useFormState does on mount once isValid is read: validates the form, so isValid is known
  // before the first change - the subscription alone only gets it from the next validation on.
  useEffect(() => {
    control._setValid(true);
  }, [control]);

  // A new canSave (e.g. once the connector mappings are loaded) re-checks the current values with it.
  useEffect(() => {
    canSaveRef.current = canSave;
    scheduleSave(getValues());
  }, [canSave, getValues, scheduleSave]);

  // A new calculation (loaded, saved, or no longer savable) re-checks the current values against it.
  useEffect(() => {
    calculationRef.current = calculation;
    scheduleSave(getValues());
  }, [calculation, getValues, scheduleSave]);
}
